import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/app/api";
import { useAuth } from "@/app/AuthContext";
import {
  clearJourneySession,
  dismissTransferPrompt,
  loadJourneySession,
  loadLatestEligibilityResult,
  transferPromptDismissed,
} from "@/lib/journey";

export const JourneyTransferPrompt = () => {
  const { user } = useAuth();
  const [journey, setJourney] = useState(null);
  const [latestResult, setLatestResult] = useState(null);
  const [includeJourney, setIncludeJourney] = useState(true);
  const [includeAssessment, setIncludeAssessment] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const answers = loadJourneySession();
    const result = loadLatestEligibilityResult();
    if ((Object.keys(answers).length > 0 || result) && !transferPromptDismissed()) {
      setJourney(answers);
      setLatestResult(result);
    }
  }, []);

  if (!journey || user?.role !== "individual") return null;

  const closeForSession = () => {
    dismissTransferPrompt();
    setJourney(null);
  };

  const save = async () => {
    if (!includeJourney && !includeAssessment) {
      toast.error("Choose at least one item to save, or select Discard.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/individual/navigator/transfer", {
        journey_answers: includeJourney ? journey : {},
        latest_eligibility_result: includeAssessment ? latestResult : null,
        confirm: true,
      });
      clearJourneySession();
      setJourney(null);
      toast.success("Journey information saved to your profile.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    clearJourneySession();
    setJourney(null);
    toast.success("Temporary journey information discarded.");
  };

  return (
    <div data-testid="journey-transfer-prompt" className="fixed inset-x-4 bottom-24 z-[70] mx-auto max-w-xl rounded-3xl border border-cobalt/20 bg-white p-5 shadow-2xl lg:bottom-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-cobalt/10 text-cobalt"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h2 className="font-display text-xl font-bold text-ink">Save your journey?</h2>
            <p className="mt-1 text-sm leading-6 text-slatey">
              You answered journey questions before signing in. OpenDegree can save them to your profile so you can continue across the platform.
            </p>
          </div>
        </div>
        <button onClick={closeForSession} data-testid="journey-transfer-dismiss" className="rounded-full p-2 text-slatey hover:bg-surface" aria-label="Not now"><X className="h-5 w-5" /></button>
      </div>
      <div className="mt-4 rounded-2xl bg-surface p-3 text-xs leading-5 text-slatey">
        Subject: {journey.subject_interest || "Not provided"} · Funding: {(journey.funding_intention || "not provided").replaceAll("_", " ")} · UK status: {(journey.uk_status || "not provided").replaceAll("_", " ")}
      </div>
      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-2 text-sm font-semibold text-ink">
          <input type="checkbox" checked={includeJourney} onChange={(event) => setIncludeJourney(event.target.checked)} data-testid="transfer-include-journey" className="mt-1 h-4 w-4 accent-cobalt" />
          Save journey answers (subject, funding intention, residence/status and education context)
        </label>
        {latestResult && (
          <label className="flex items-start gap-2 text-sm font-semibold text-ink">
            <input type="checkbox" checked={includeAssessment} onChange={(event) => setIncludeAssessment(event.target.checked)} data-testid="transfer-include-assessment" className="mt-1 h-4 w-4 accent-cobalt" />
            Save latest indicative eligibility assessment
          </label>
        )}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <button onClick={save} disabled={saving} data-testid="journey-transfer-confirm" className="rounded-full bg-cobalt px-4 py-3 text-sm font-bold text-white disabled:opacity-50">Save to profile</button>
        <button onClick={closeForSession} data-testid="journey-transfer-later" className="rounded-full border border-slate-300 px-4 py-3 text-sm font-bold text-ink">Not now</button>
        <button onClick={discard} data-testid="journey-transfer-discard" className="rounded-full border border-rose-200 px-4 py-3 text-sm font-bold text-rose-600">Discard</button>
      </div>
    </div>
  );
};
